/**
 * ConfirmDialog — 토스 스타일 확인 대화상자 (브라우저 기본 확인창 대체)
 *
 * Dialog(size="sm") + ModalActions 위에 올린 얇은 조합이다. 스크림·라운드·그림자·포커스 트랩·ESC·
 * 스크롤 잠금·레이어 등록(열려 있는 동안 토스트가 기다린다)은 모두 Dialog가 맡는다.
 *
 * 두 가지 사용법:
 *
 * 1) 컴포넌트로 직접 렌더 (loading 상태가 필요할 때)
 *    <ConfirmDialog
 *      open={open}
 *      title="이 대화를 삭제할까요?"
 *      description="삭제한 대화는 되돌릴 수 없어요"
 *      confirmLabel="삭제하기"
 *      danger
 *      loading={isDeleting}
 *      onConfirm={handleDelete}
 *      onCancel={() => setOpen(false)}
 *    />
 *
 * 2) 훅으로 Promise처럼 사용 (App.js의 ConfirmDialogProvider가 렌더를 담당)
 *    const confirmDialog = useConfirm();   // 변수명은 confirmDialog로 — 브라우저 기본 확인창 검색에 걸리지 않게
 *    if (!(await confirmDialog({ title: '삭제할까요?', confirmLabel: '삭제하기', danger: true }))) return;
 *
 * Props
 *   open            : boolean
 *   title           : string
 *   description     : string | ReactNode (문자열은 줄바꿈(\n) 유지)
 *   confirmLabel    : 동사형 라벨 ("삭제하기", "공유하기") — "확인" 같은 모호한 라벨 금지
 *   cancelLabel     : 기본 "취소"
 *   danger          : 파괴적 동작이면 true → 빨간 primary(#F04452), 초기 포커스는 취소 버튼
 *   loading         : true면 버튼 비활성 + 스피너, ESC/배경 클릭으로 닫히지 않음
 *   secondaryAction : { label, onClick } — 보조 행동 ("엑셀 저장하기"). 텍스트 버튼으로 놓이고, 눌러도 대화상자는 유지
 *   onConfirm / onCancel
 *
 * 레이아웃 (ModalActions 규칙)
 *   >600px : 중앙 다이얼로그 (16px 라운드, 400px), 버튼 가로 [보조(텍스트, 왼쪽)] … [취소][primary]
 *   ≤600px : 바텀시트 (상단 16px 라운드, safe-area), 버튼 세로 스택 primary → 취소 → 보조
 *
 * 접근성
 *   role="dialog" aria-modal aria-labelledby aria-describedby, 포커스 트랩, ESC 닫기,
 *   열릴 때 primary(위험 동작이면 취소)로 포커스, 닫힐 때 원래 위치로 포커스 복귀, 바디 스크롤 잠금.
 *
 * 테스트·E2E 셀렉터: confirm-dialog-overlay / confirm-dialog(패널) / confirm-dialog-confirm /
 *   confirm-dialog-cancel / confirm-dialog-secondary, 패널 클래스 whatquiz-confirm-panel(--danger|--loading)
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import Dialog, { DIALOG_SHEET_MEDIA_QUERY } from './Dialog';
import ModalActions from './ModalActions';
import './ConfirmDialog.css';

export const CONFIRM_SHEET_MEDIA_QUERY = DIALOG_SHEET_MEDIA_QUERY;
// HtmlCanvasEditorModal(100001) 위에서도 열려야 한다.
export const CONFIRM_DIALOG_Z_INDEX = 100100;

const joinClassNames = (...values) => values.filter(Boolean).join(' ');

const isSecondaryAction = (action) =>
  Boolean(action && typeof action === 'object' && action.label);

const ConfirmDialog = ({
  open = false,
  title,
  description = null,
  confirmLabel = '확인',
  cancelLabel = '취소',
  danger = false,
  loading = false,
  onConfirm,
  onCancel,
  secondaryAction = null,
}) => {
  const confirmButtonRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const hasSecondary = isSecondaryAction(secondaryAction);

  const handleSecondaryClick = () => {
    if (loading) return;
    secondaryAction.onClick?.();
  };

  const footer = (
    <ModalActions
      primary={{
        label: confirmLabel,
        onClick: onConfirm,
        loading,
        danger,
        ref: confirmButtonRef,
        testId: 'confirm-dialog-confirm',
      }}
      secondary={{
        label: cancelLabel,
        onClick: onCancel,
        ref: cancelButtonRef,
        testId: 'confirm-dialog-cancel',
      }}
      tertiary={hasSecondary ? {
        label: secondaryAction.label,
        onClick: handleSecondaryClick,
        testId: 'confirm-dialog-secondary',
      } : null}
      testId="confirm-dialog-actions"
    />
  );

  return (
    <Dialog
      aria-busy={loading}
      description={description}
      dismissible={!loading}
      footer={footer}
      initialFocus={danger ? cancelButtonRef : confirmButtonRef}
      onClose={onCancel}
      open={open}
      panelClassName={joinClassNames(
        'whatquiz-confirm-panel',
        danger && 'whatquiz-confirm-panel--danger',
        loading && 'whatquiz-confirm-panel--loading',
      )}
      showClose={false}
      size="sm"
      testId="confirm-dialog"
      title={title}
      zIndex={CONFIRM_DIALOG_Z_INDEX}
    />
  );
};

// ---------------------------------------------------------------------------
// Provider + useConfirm (Promise<boolean>)
// ---------------------------------------------------------------------------

const ConfirmContext = createContext(null);

let warnedWithoutProvider = false;
// Provider 밖(격리 렌더·테스트)에서 호출되면 아무 것도 실행하지 않도록 취소(false)로 처리한다.
const confirmWithoutProvider = (options) => {
  if (process.env.NODE_ENV !== 'production' && !warnedWithoutProvider) {
    warnedWithoutProvider = true;
    // eslint-disable-next-line no-console
    console.warn(
      '[ConfirmDialog] ConfirmDialogProvider 밖에서 useConfirm이 호출되어 취소로 처리했어요.',
      options?.title,
    );
  }
  return Promise.resolve(false);
};

/**
 * const confirmDialog = useConfirm();
 * const ok = await confirmDialog({ title, description, confirmLabel, cancelLabel, danger, secondaryAction });
 */
export const useConfirm = () => {
  const confirm = useContext(ConfirmContext);
  return confirm || confirmWithoutProvider;
};

export const ConfirmDialogProvider = ({ children }) => {
  const [request, setRequest] = useState(null);
  const requestRef = useRef(null);

  const settle = useCallback((value) => {
    const current = requestRef.current;
    requestRef.current = null;
    setRequest(null);
    current?.resolve(value);
  }, []);

  const confirm = useCallback((options = {}) => new Promise((resolve) => {
    // 이미 열려 있는 요청이 있으면 취소로 정리하고 새 요청을 띄운다
    requestRef.current?.resolve(false);
    const next = { options: options || {}, resolve };
    requestRef.current = next;
    setRequest(next);
  }), []);

  useEffect(() => () => {
    requestRef.current?.resolve(false);
    requestRef.current = null;
  }, []);

  const options = request?.options || {};

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={Boolean(request)}
        title={options.title}
        description={options.description}
        confirmLabel={options.confirmLabel}
        cancelLabel={options.cancelLabel}
        danger={Boolean(options.danger)}
        secondaryAction={options.secondaryAction}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
      />
    </ConfirmContext.Provider>
  );
};

export default ConfirmDialog;
